import { ComponentType, FC, memo, useMemo, useState } from 'react';

import { PreparedVariation, VariantMatrix } from '@telialt/models/src/EshopModel';
import {
    findByPathFromRoot,
    findLeafByCode,
    getFirstInStockLeaf,
    getFirstLeaf,
    toPreparedVariations,
} from '@telialt/utils/src/eshopUtils';
import { useComponentId } from '@telialt/utils/src/hooks';

import { ESHOP_TEST_ID } from '../../test/test-ids';
import { BadgeBlock } from './BadgeBlock';
import { BadgeBlockAdditionalInfo } from './BadgeBlockAdditionalInfo';
import { BadgeBlockTitleByAlias } from './BadgeBlockTitleByAlias';
import { BadgeBlockContentProps, BadgeBlockInfoProps, BadgeBlockTitleProps, MapToBadgeFunction } from './model';
import { VariantSelectorSection } from './ProductVariationBadges.styles';

type Props = {
    variantMatrix: VariantMatrix[];
    onSelect: (variation: PreparedVariation) => void;
    /** @name productInCartCode
     * @see selectProductInCartCode*/
    productInCartCode: string;
    mapToBadge: MapToBadgeFunction;
    className?: string;
    BadgeBlockTitle?: ComponentType<BadgeBlockTitleProps>;
    BadgeBlockContent?: ComponentType<BadgeBlockContentProps>;
    BadgeBlockInfo?: ComponentType<BadgeBlockInfoProps>;
    isDisabled?: boolean;
};

const ProductVariationBadges: FC<Props> = ({
    variantMatrix,
    onSelect,
    productInCartCode,
    mapToBadge,
    className,
    BadgeBlockTitle = BadgeBlockTitleByAlias,
    BadgeBlockContent = BadgeBlock,
    BadgeBlockInfo = BadgeBlockAdditionalInfo,
    isDisabled = false,
}) => {
    const uniqueId = useComponentId();
    const preparedVariations = useMemo(() => toPreparedVariations(variantMatrix), [variantMatrix]);
    const [pathFromRoot, setPathFromRoot] = useState(
        () => findLeafByCode(preparedVariations, productInCartCode).pathFromRoot,
    );

    const currentLevel1 = findByPathFromRoot(preparedVariations, [pathFromRoot[0]]);
    const currentLevel2 = findByPathFromRoot(preparedVariations, [pathFromRoot[0], pathFromRoot[1]]);
    const currentLevel3 = findByPathFromRoot(preparedVariations, [pathFromRoot[0], pathFromRoot[1], pathFromRoot[2]]);
    const currentLevel4 = findByPathFromRoot(preparedVariations, [
        pathFromRoot[0],
        pathFromRoot[1],
        pathFromRoot[2],
        pathFromRoot[3],
    ]);

    const level1Id = useComponentId('level-1');
    const level2Id = useComponentId('level-2');
    const level3Id = useComponentId('level-3');
    const level4Id = useComponentId('level-4');

    /**
     * @function
     * @name predictFutureSelection
     * @param key PreparedVariation.key
     * @param level Level/Depth [number starting from 1...N]
     */
    const predictFutureSelection = (key: string, level: number) => {
        const futurePathToLeaf = [...pathFromRoot];
        futurePathToLeaf[level - 1] = key;

        const futureLeaf = findByPathFromRoot(preparedVariations, futurePathToLeaf);

        const futureSameLevelPathFromRoot = futurePathToLeaf.slice(0, level);
        const futureSameLevel = findByPathFromRoot(preparedVariations, futureSameLevelPathFromRoot);
        const inStockLeafNeighbour = getFirstInStockLeaf(futureSameLevel?.elements);
        const firstLeafNeighbour = getFirstLeaf(futureSameLevel?.elements);

        const result =
            !!futureLeaf && futureLeaf.isOutOfStock === false
                ? futureLeaf
                : inStockLeafNeighbour || futureLeaf || firstLeafNeighbour;

        return result;
    };

    const handleSelect = (key: string, level: number) => {
        const futureLeaf = predictFutureSelection(key, level);
        setPathFromRoot(futureLeaf.pathFromRoot);

        onSelect(futureLeaf);
    };

    const convertPreparedVariationToBadge = (node: PreparedVariation) => {
        const selectedKey = pathFromRoot[node.depthLvl - 1];
        const isChecked = node.key === selectedKey;
        const isDisabledLooking = node.isOutOfStock;
        const onClick = () => handleSelect(node.key, node.depthLvl);
        const dataTest = `btn--${node.alias}-${node.code}`;

        return mapToBadge(node, {
            checked: isChecked,
            disabled: isDisabled,
            disabledLooking: isDisabledLooking,
            dataTest,
            onClick,
            parentUID: uniqueId,
        });
    };

    return (
        <VariantSelectorSection data-test={ESHOP_TEST_ID.PRODUCT_VARIANT_SELECTOR} className={className}>
            <BadgeBlockTitle id={level1Id} variation={currentLevel1} />
            <BadgeBlockContent ariaLabelledBy={level1Id}>
                {preparedVariations?.map(convertPreparedVariationToBadge)}
            </BadgeBlockContent>
            <BadgeBlockInfo variation={currentLevel1} />

            <BadgeBlockTitle id={level2Id} variation={currentLevel2} />
            <BadgeBlockContent ariaLabelledBy={level2Id}>
                {currentLevel1?.elements?.map(convertPreparedVariationToBadge)}
            </BadgeBlockContent>
            <BadgeBlockInfo variation={currentLevel2} />

            <BadgeBlockTitle id={level3Id} variation={currentLevel3} />
            <BadgeBlockContent ariaLabelledBy={level3Id}>
                {currentLevel2?.elements?.map(convertPreparedVariationToBadge)}
            </BadgeBlockContent>
            <BadgeBlockInfo variation={currentLevel3} />

            <BadgeBlockTitle id={level4Id} variation={currentLevel4} />
            <BadgeBlockContent ariaLabelledBy={level4Id}>
                {currentLevel3?.elements?.map(convertPreparedVariationToBadge)}
            </BadgeBlockContent>
            <BadgeBlockInfo variation={currentLevel4} />
        </VariantSelectorSection>
    );
};

export const ProductVariationBadgesMemo = memo(ProductVariationBadges);
